import { styled } from "@mui/material";
import { useCallback } from "react";

import { useTranslation } from "@/lib/i18n";
import { Serie } from "@/types/serie";
import { useDashboardData } from "@/features/shared/hooks";
import { AdditionalWidgetData } from "@/types/additional-widget-data";
import { TimeSeriesTable } from "@/features/shared/widgets/components";
import useRefreshWidget from "@/features/shared/widgets/hooks/useRefetchWidget";

const StyledArrow = styled("span")<{ serieName: string; value: number }>(
  ({ serieName, theme, value }) => ({
    display: "inline-block",
    transform: `rotate(${value}deg)`,
    fontWeight: serieName.includes("Model") ? "normal" : "bold",
    color: theme.palette.primary.main,
    fontSize: "1.5em",
  }),
);

/**
 * Render the arrow turned with the right amount of deg
 */
const renderValue = (value: number, serie: Serie) => {
  return (
    <StyledArrow value={value} serieName={serie.name}>
      ↓
    </StyledArrow>
  );
};

const tableHeaderFormatter = (serie: Serie, t: (key: string) => string) => {
  const serieName = String(serie.name || serie.translatedName).toLocaleLowerCase();
  const isObservationSerie = serieName.includes("obser");
  const isModelSerie = serieName.includes("model");

  if (isObservationSerie) {
    return t("widgets.observation");
  }

  if (isModelSerie) {
    return t("widgets.model");
  }

  return serieName;
};

type Props = {
  id: string;
  data: AdditionalWidgetData;
};

const WindDirection = ({ id, data }: Props) => {
  const { selectedTimeRange: timeRange } = useDashboardData();
  const { t } = useTranslation();

  useRefreshWidget(id);

  const formatTableHead = useCallback(
    (serie: Serie) => tableHeaderFormatter(serie, t),
    [t],
  );

  return (
    <TimeSeriesTable
      alwaysShowValues
      series={data.data}
      timeRange={timeRange}
      valueRenderer={renderValue}
      emptyValueRenderer={() => ""}
      tableHeadFormatter={formatTableHead}
    />
  );
};

export default WindDirection;
